import { type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import type { AccountStatus, Approacher } from '../types';

import { STATUS_LABELS, STATUS_VARIANTS } from './account-status-meta';

const LABELS = STATUS_LABELS;
const VARIANTS = STATUS_VARIANTS;

export function AccountStatusBadge({
  status,
}: {
  status: AccountStatus;
}): ReactNode {
  return (
    <Badge variant={VARIANTS[status] ?? 'default'}>
      {LABELS[status] ?? status}
    </Badge>
  );
}

type StatusVar = { id: string; status: string };
type MemberList = Approacher[] | undefined;
const QK = ['team-members'];

async function optimisticSet(
  qc: ReturnType<typeof useQueryClient>,
  v: StatusVar
): Promise<{ prev: MemberList }> {
  await qc.cancelQueries({ queryKey: QK });
  const prev = qc.getQueryData<MemberList>(QK);
  qc.setQueryData<MemberList>(QK, (old) =>
    old?.map((m) =>
      m.id === v.id ? { ...m, account_status: v.status as AccountStatus } : m
    )
  );
  return { prev };
}

function useSetAccountStatus(): {
  mutate: (v: StatusVar) => void;
  isPending: boolean;
} {
  const qc = useQueryClient();
  const { toast } = useToast();
  const m = useMutation({
    mutationFn: (v: StatusVar) =>
      pb.send(`/api/team-members/status/${v.id}`, {
        method: 'POST',
        body: { status: v.status, force: true },
      }),
    onMutate: (v) => optimisticSet(qc, v),
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(QK, ctx.prev);
      toast('Error al cambiar estado', 'error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QK }),
  });
  return { mutate: m.mutate, isPending: m.isPending };
}

// 'deleted' intentionally omitted — use the Archive button, not this dropdown.
const STATUSES: AccountStatus[] = [
  'healthy',
  'test',
  'pre_banned',
  'banned',
  'failed',
];

export function AccountStatusSelect({
  member,
}: {
  member: Approacher;
}): ReactNode {
  const { mutate, isPending } = useSetAccountStatus();
  return (
    <select
      value={member.account_status}
      disabled={isPending}
      onChange={(e) => mutate({ id: member.id, status: e.target.value })}
      className="rounded-md border border-white/[0.06] bg-surface px-sm py-0.5 text-[11px] text-text-secondary focus:border-brand-red focus:outline-none disabled:opacity-30"
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>
          {LABELS[s]}
        </option>
      ))}
    </select>
  );
}
